import React, { useMemo, useState } from "react";

function DeviceMonitoringPage({ users }) {
  const [searchText, setSearchText] =
    useState("");

  const getTime = (value) => {
    if (!value) return 0;

    if (value?.seconds) {
      return value.seconds * 1000;
    }

    return Number(value || 0);
  };

  const formatDate = (value) => {
    const time = getTime(value);

    if (!time) {
      return "—";
    }

    return new Date(
      time
    ).toLocaleString();
  };

  /*
   * Build an ownership map for every installation ID.
   *
   * This lets the owner see when the same physical
   * app installation has appeared under more than
   * one X-AUCTIONS account.
   */
  const installationOwners =
    useMemo(() => {
      const ownerMap = {};

      users.forEach((user) => {
        const userKey =
          user.uid ||
          user.id ||
          user.email ||
          "";

        if (!userKey) {
          return;
        }

        const installationIds =
          Array.isArray(
            user.installationIds
          )
            ? [
                ...new Set(
                  user.installationIds
                    .filter(Boolean)
                    .map((item) =>
                      String(item)
                    )
                ),
              ]
            : [];

        installationIds.forEach(
          (installationId) => {
            if (
              !ownerMap[
                installationId
              ]
            ) {
              ownerMap[
                installationId
              ] = new Set();
            }

            ownerMap[
              installationId
            ].add(userKey);
          }
        );
      });

      return ownerMap;
    }, [users]);

  const deviceRows =
    useMemo(() => {
      const now = Date.now();

      const oneDayMs =
        24 *
        60 *
        60 *
        1000;

      const thirtyDaysMs =
        30 *
        oneDayMs;

      return users
        .map((user) => {
          const installationIds =
            Array.isArray(
              user.installationIds
            )
              ? [
                  ...new Set(
                    user.installationIds
                      .filter(Boolean)
                      .map((item) =>
                        String(item)
                      )
                  ),
                ]
              : [];

          const installationCount =
            installationIds.length;

          const sharedInstallationIds =
            installationIds.filter(
              (installationId) =>
                Number(
                  installationOwners[
                    installationId
                  ]?.size || 0
                ) > 1
            );

          const linkedAccountKeys =
            new Set();

          sharedInstallationIds.forEach(
            (installationId) => {
              const owners =
                installationOwners[
                  installationId
                ];

              if (!owners) {
                return;
              }

              owners.forEach(
                (ownerKey) => {
                  linkedAccountKeys.add(
                    ownerKey
                  );
                }
              );
            }
          );

          const thisUserKey =
            user.uid ||
            user.id ||
            user.email ||
            "";

          if (thisUserKey) {
            linkedAccountKeys.delete(
              thisUserKey
            );
          }

          const linkedAccountCount =
            linkedAccountKeys.size;

          const lastLoginTime =
            getTime(
              user.lastLoginAt
            ) ||
            Number(
              user.lastLoginAtMs ||
                0
            );

          const lastSeenTime =
            getTime(
              user.lastInstallationSeenTimestamp
            ) ||
            Number(
              user.lastInstallationSeenAt ||
                0
            );

          const effectiveActivityTime =
            Math.max(
              lastLoginTime,
              lastSeenTime
            );

          const activityAgeMs =
            effectiveActivityTime
              ? now -
                effectiveActivityTime
              : 0;

          const riskFlags = [];

          /*
           * Cross-account installation reuse is
           * the strongest device-monitoring signal
           * currently available in our data.
           */
          if (
            sharedInstallationIds.length >
            0
          ) {
            riskFlags.push(
              `Shared Installation (${sharedInstallationIds.length})`
            );
          }

          if (
            linkedAccountCount > 0
          ) {
            riskFlags.push(
              `Linked Accounts (${linkedAccountCount})`
            );
          }

          /*
           * Many historical installation IDs
           * alone are not proof of abuse because
           * reinstalling/dev builds can generate
           * new IDs. Keep it as a review signal.
           */
          if (
            installationCount >= 5
          ) {
            riskFlags.push(
              "Many Installations"
            );
          }

          if (user.banned) {
            riskFlags.push(
              "Banned Account"
            );
          }

          if (user.disabled) {
            riskFlags.push(
              "Disabled Account"
            );
          }

          if (
            !user.deviceName &&
            !user.deviceModel
          ) {
            riskFlags.push(
              "Missing Device Info"
            );
          }

          if (
            !effectiveActivityTime
          ) {
            riskFlags.push(
              "No Activity Time"
            );
          } else if (
            activityAgeMs >=
            thirtyDaysMs
          ) {
            riskFlags.push(
              "Inactive 30+ Days"
            );
          }

          return {
            id:
              user.id ||
              user.uid ||
              user.email,

            username:
              user.username ||
              "—",

            email:
              user.email ||
              "—",

            uid:
              user.uid ||
              user.id ||
              "—",

            deviceName:
              user.deviceName ||
              user.pushTokenDevice
                ?.deviceName ||
              "—",

            deviceModel:
              user.deviceModel ||
              user.pushTokenDevice
                ?.modelName ||
              "—",

            platform:
              user.platform ||
              user.pushTokenDevice
                ?.platform ||
              "—",

            osVersion:
              user.osVersion ||
              user.pushTokenDevice
                ?.osVersion ||
              "—",

            lastLoginAt:
              user.lastLoginAt,

            lastLoginAtMs:
              user.lastLoginAtMs,

            lastInstallationSeenTimestamp:
              user.lastInstallationSeenTimestamp,

            lastInstallationSeenAt:
              user.lastInstallationSeenAt,

            lastInstallationId:
              user.lastInstallationId ||
              "—",

            installationIds,

            installationCount,

            sharedInstallationIds,

            linkedAccountCount,

            currentSessionId:
              user.currentSessionId ||
              "—",

            riskFlags,

            effectiveActivityTime,

            isAdmin:
              user.isAdmin === true,

            isOwner:
              user.isOwner === true,
          };
        })
        .sort((a, b) => {
          return (
            Number(
              b.effectiveActivityTime ||
                0
            ) -
            Number(
              a.effectiveActivityTime ||
                0
            )
          );
        });
    }, [
      users,
      installationOwners,
    ]);

  const filteredRows =
    useMemo(() => {
      const search =
        searchText
          .toLowerCase()
          .trim();

      if (!search) {
        return deviceRows;
      }

      return deviceRows.filter(
        (row) => {
          return (
            String(
              row.username || ""
            )
              .toLowerCase()
              .includes(search) ||

            String(
              row.email || ""
            )
              .toLowerCase()
              .includes(search) ||

            String(
              row.uid || ""
            )
              .toLowerCase()
              .includes(search) ||

            String(
              row.deviceName || ""
            )
              .toLowerCase()
              .includes(search) ||

            String(
              row.deviceModel || ""
            )
              .toLowerCase()
              .includes(search) ||

            String(
              row.platform || ""
            )
              .toLowerCase()
              .includes(search) ||

            String(
              row.osVersion || ""
            )
              .toLowerCase()
              .includes(search) ||

            String(
              row.lastInstallationId ||
                ""
            )
              .toLowerCase()
              .includes(search) ||

            String(
              row.currentSessionId ||
                ""
            )
              .toLowerCase()
              .includes(search) ||

            row.installationIds.some(
              (installationId) =>
                String(
                  installationId
                )
                  .toLowerCase()
                  .includes(search)
            )
          );
        }
      );
    }, [
      deviceRows,
      searchText,
    ]);

  const usersWithDeviceInfo =
    deviceRows.filter(
      (row) =>
        row.deviceName !== "—" ||
        row.deviceModel !== "—"
    ).length;

  const androidUsers =
    deviceRows.filter(
      (row) =>
        String(
          row.platform || ""
        ).toLowerCase() ===
        "android"
    ).length;

  const iosUsers =
    deviceRows.filter(
      (row) =>
        String(
          row.platform || ""
        ).toLowerCase() ===
        "ios"
    ).length;

  const highInstallationUsers =
    deviceRows.filter(
      (row) =>
        row.installationCount >= 5
    ).length;

  const usersMissingDeviceInfo =
    deviceRows.filter(
      (row) =>
        row.deviceName === "—" &&
        row.deviceModel === "—"
    ).length;

  const sharedInstallationUsers =
    deviceRows.filter(
      (row) =>
        row.sharedInstallationIds
          .length > 0
    ).length;

  const linkedAccountUsers =
    deviceRows.filter(
      (row) =>
        row.linkedAccountCount > 0
    ).length;

  const uniqueSharedInstallations =
    Object.values(
      installationOwners
    ).filter(
      (owners) =>
        Number(
          owners?.size || 0
        ) > 1
    ).length;

  return (
    <>
      <section className="stats-grid">
        <div className="stat-card">
          <span>
            Total Users
          </span>

          <strong>
            {deviceRows.length}
          </strong>
        </div>

        <div className="stat-card">
          <span>
            Users With Device Info
          </span>

          <strong>
            {usersWithDeviceInfo}
          </strong>
        </div>

        <div className="stat-card">
          <span>
            Android Users
          </span>

          <strong>
            {androidUsers}
          </strong>
        </div>

        <div className="stat-card">
          <span>
            iOS Users
          </span>

          <strong>
            {iosUsers}
          </strong>
        </div>

        <div className="stat-card">
          <span>
            Shared Installations
          </span>

          <strong>
            {
              uniqueSharedInstallations
            }
          </strong>

          <small
            style={{
              display: "block",
              marginTop: 4,
            }}
          >
            Installation IDs linked
            to multiple accounts
          </small>
        </div>

        <div className="stat-card">
          <span>
            Users On Shared Installations
          </span>

          <strong>
            {
              sharedInstallationUsers
            }
          </strong>
        </div>

        <div className="stat-card">
          <span>
            Users With Linked Accounts
          </span>

          <strong>
            {linkedAccountUsers}
          </strong>
        </div>

        <div className="stat-card">
          <span>
            High Installation Count
          </span>

          <strong>
            {
              highInstallationUsers
            }
          </strong>

          <small
            style={{
              display: "block",
              marginTop: 4,
            }}
          >
            5 or more historical
            installation IDs
          </small>
        </div>

        <div className="stat-card">
          <span>
            Missing Device Info
          </span>

          <strong>
            {
              usersMissingDeviceInfo
            }
          </strong>
        </div>
      </section>

      <section className="table-card">
        <div className="table-header">
          <div>
            <h2>
              Device Monitoring
            </h2>

            <p>
              Review device,
              installation, account-link
              and session signals for
              X-AUCTIONS users.
            </p>
          </div>
        </div>

        <div className="controls-row">
          <input
            className="search-input"
            placeholder="Search username, email, UID, device, installation ID, session ID, platform, or OS..."
            value={searchText}
            onChange={(event) =>
              setSearchText(
                event.target.value
              )
            }
          />
        </div>

        {filteredRows.length ===
        0 ? (
          <p className="empty-text">
            No device records match
            this search.
          </p>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>User</th>

                  <th>Email</th>

                  <th>UID</th>

                  <th>
                    Current Device
                  </th>

                  <th>
                    Device Model
                  </th>

                  <th>
                    Platform
                  </th>

                  <th>
                    OS Version
                  </th>

                  <th>
                    Last Login
                  </th>

                  <th>
                    Last Device Seen
                  </th>

                  <th>
                    Installations
                  </th>

                  <th>
                    Shared Installations
                  </th>

                  <th>
                    Linked Accounts
                  </th>

                  <th>
                    Last Installation ID
                  </th>

                  <th>
                    Current Session ID
                  </th>

                  <th>
                    Review Signals
                  </th>
                </tr>
              </thead>

              <tbody>
                {filteredRows.map(
                  (row) => (
                    <tr key={row.id}>
                      <td>
                        <strong>
                          {
                            row.username
                          }
                        </strong>

                        {row.isOwner ? (
                          <div className="small-muted">
                            Owner
                          </div>
                        ) : row.isAdmin ? (
                          <div className="small-muted">
                            Admin
                          </div>
                        ) : null}
                      </td>

                      <td>
                        {row.email}
                      </td>

                      <td className="small-muted">
                        {row.uid}
                      </td>

                      <td>
                        {
                          row.deviceName
                        }
                      </td>

                      <td>
                        {
                          row.deviceModel
                        }
                      </td>

                      <td>
                        {
                          row.platform
                        }
                      </td>

                      <td>
                        {
                          row.osVersion
                        }
                      </td>

                      <td>
                        {formatDate(
                          row.lastLoginAt ||
                            row.lastLoginAtMs
                        )}
                      </td>

                      <td>
                        {formatDate(
                          row.lastInstallationSeenTimestamp ||
                            row.lastInstallationSeenAt
                        )}
                      </td>

                      <td>
                        {
                          row.installationCount
                        }
                      </td>

                      <td>
                        {row
                          .sharedInstallationIds
                          .length > 0 ? (
                          <>
                            <strong>
                              {
                                row
                                  .sharedInstallationIds
                                  .length
                              }
                            </strong>

                            <div className="small-muted">
                              {row.sharedInstallationIds
                                .slice(
                                  0,
                                  2
                                )
                                .join(
                                  " | "
                                )}

                              {row
                                .sharedInstallationIds
                                .length >
                              2
                                ? ` | +${
                                    row
                                      .sharedInstallationIds
                                      .length -
                                    2
                                  } more`
                                : ""}
                            </div>
                          </>
                        ) : (
                          "—"
                        )}
                      </td>

                      <td>
                        {row.linkedAccountCount >
                        0 ? (
                          <strong>
                            {
                              row.linkedAccountCount
                            }
                          </strong>
                        ) : (
                          "—"
                        )}
                      </td>

                      <td className="small-muted">
                        {
                          row.lastInstallationId
                        }
                      </td>

                      <td className="small-muted">
                        {
                          row.currentSessionId
                        }
                      </td>

                      <td className="small-muted">
                        {row.riskFlags
                          .length
                          ? row.riskFlags.join(
                              " | "
                            )
                          : "No review signals"}
                      </td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}

export default DeviceMonitoringPage;